import { viruses } from "@prisma/client";

/**
 * Adds the current context as a query parameter to a URL.
 * Preserves existing query parameters and handles both internal and external URLs.
 *
 * @param href - The target URL (can be relative or absolute)
 * @param context - The virus context to add
 * @returns The URL with context parameter added
 *
 * @example
 * addContextToUrl('/explore', 'DENV') // '/explore?context=DENV'
 * addContextToUrl('/explore?tab=map', 'DENV') // '/explore?tab=map&context=DENV'
 * addContextToUrl('https://example.com', 'DENV') // 'https://example.com' (external, unchanged)
 */
export function addContextToUrl(href: string, context: viruses): string {
  // Don't modify external URLs, anchors, or special Next.js routes
  if (
    !href ||
    href.startsWith("http://") ||
    href.startsWith("https://") ||
    href.startsWith("#") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:")
  ) {
    return href;
  }

  try {
    // Split pathname, query string, and hash
    const hashIndex = href.indexOf("#");
    const queryIndex = href.indexOf("?");

    let pathname = href;
    let search = "";
    let hash = "";

    if (hashIndex !== -1) {
      hash = href.slice(hashIndex);
      pathname = href.slice(0, hashIndex);
    }

    if (queryIndex !== -1 && (hashIndex === -1 || queryIndex < hashIndex)) {
      search = pathname.slice(queryIndex + 1);
      pathname = pathname.slice(0, queryIndex);
    }

    const params = new URLSearchParams(search);

    // Add or update context parameter
    params.set("context", context);

    const newSearch = params.toString();
    return `${pathname}${newSearch ? "?" + newSearch : ""}${hash}`;
  } catch (error) {
    console.error("Error adding context to URL:", error);
    return href;
  }
}

/**
 * Extracts the context from the current URL or returns the default.
 *
 * @param defaultContext - The default context to return if none is found
 * @returns The virus context from the URL or the default
 */
export function getContextFromUrl(defaultContext: viruses = "DENV"): viruses {
  if (typeof window === "undefined") {
    return defaultContext;
  }

  const params = new URLSearchParams(window.location.search);
  const contextParam = params.get("context");

  // Validate that it's a valid virus context
  if (
    contextParam &&
    (contextParam === viruses.DENV ||
      contextParam === viruses.CHIKV ||
      contextParam === viruses.ZIKV) //||
      // contextParam === viruses.WNV)
  ) {
    return contextParam as viruses;
  }

  return defaultContext;
}

/**
 * Updates the current URL with a new context without navigation.
 *
 * @param context - The new virus context
 */
export function updateUrlContext(context: viruses): void {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.set("context", context);
  window.history.replaceState({}, "", url.toString());
}
